import { useState, useEffect, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { FileText, Plus, X, Loader2, Ban, Check, Printer, Send, Truck, Pencil, MapPin, Search, Weight, Wallet, Boxes, User, ArrowRight } from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney, formatDateTime } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'

interface MDFe {
  id: number
  numero: number
  serie: number
  status: string
  data_emissao: string
  data_encerramento: string | null
  uf_ini: string
  uf_fim: string
  cidade_carregamento: string | null
  cidade_descarregamento: string | null
  veiculo_id: number
  motorista_id: number | null
  motorista_nome: string | null
  veiculo_placa: string | null
  tipo_transporte: string
  peso_total_kg: number
  valor_total_carga: number
  valor_frete_estimado: number
  total_documentos: number
  percurso: string[]
  observacoes: string | null
}

// status → cor do pill (texto+fundo) e cor da bolinha
const STATUS_META: Record<string, { label: string; pill: string; dot: string }> = {
  rascunho: { label: 'Rascunho', pill: 'bg-ink-100 text-ink-600', dot: 'bg-ink-400' },
  autorizado: { label: 'Autorizado', pill: 'bg-sky-50 text-sky-700 ring-1 ring-sky-200', dot: 'bg-sky-500' },
  encerrado: { label: 'Encerrado', pill: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200', dot: 'bg-emerald-500' },
  cancelado: { label: 'Cancelado', pill: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200', dot: 'bg-rose-500' },
}

// iniciais do motorista p/ avatar
function iniciais(nome: string): string {
  const p = nome.trim().split(/\s+/)
  return ((p[0]?.[0] || '') + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase()
}
// cor estável do avatar a partir do nome
const AVATAR_COR = ['bg-brand-100 text-brand-700', 'bg-sky-100 text-sky-700', 'bg-amber-100 text-amber-700',
  'bg-violet-100 text-violet-700', 'bg-emerald-100 text-emerald-700', 'bg-rose-100 text-rose-700']
function avatarCor(nome: string): string {
  let h = 0; for (const c of nome) h = (h * 31 + c.charCodeAt(0)) | 0
  return AVATAR_COR[Math.abs(h) % AVATAR_COR.length]
}

function KpiCard({ icon, cor, label, valor, sub }: { icon: ReactNode; cor: string; label: string; valor: string; sub?: string }) {
  return (
    <div className="card card-body !p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${cor}`}>{icon}</div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-ink-400 font-medium">{label}</div>
        <div className="text-lg font-bold text-ink-800 leading-tight truncate">{valor}</div>
        {sub && <div className="text-[11px] text-ink-400 truncate">{sub}</div>}
      </div>
    </div>
  )
}

// imprime/reimprime o DAMDFE (PDF autenticado via blob)
async function imprimirDamdfe(id: number) {
  const t = toast.loading('Gerando DAMDFE...')
  try {
    const resp = await api.get(`/expedicao/mdfe/${id}/damdfe`, { responseType: 'blob' })
    const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }))
    window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 60000)
    toast.dismiss(t)
  } catch (e: any) {
    toast.dismiss(t)
    // erro vem como blob — tenta extrair a mensagem
    try {
      const txt = await e?.response?.data?.text?.()
      toast.error(JSON.parse(txt)?.detail || 'Erro ao gerar DAMDFE')
    } catch { toast.error('Erro ao gerar DAMDFE') }
  }
}

export default function ExpedicaoMDFePage() {
  const qc = useQueryClient()
  const [show, setShow] = useState(false)
  const [showCarga, setShowCarga] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [status, setStatus] = useState<string>('')
  const [page, setPage] = useState(1)
  const PAGE_SIZE = 50

  // troca de filtro volta pra primeira página
  const trocarStatus = (s: string) => { setStatus(s); setPage(1) }

  const { data, isLoading } = useQuery<{ items: MDFe[]; total: number; soma_peso: number; soma_valor: number; soma_docs: number }>({
    queryKey: ['mdfe', status, page],
    queryFn: async () => (await api.get('/expedicao/mdfe', {
      params: { status: status || undefined, page, page_size: PAGE_SIZE },
    })).data,
    placeholderData: (prev) => prev,   // mantém a tabela enquanto carrega a próxima página
  })
  const mdfes = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const { data: sefaz } = useQuery<any>({
    queryKey: ['mdfe-status-sefaz'],
    queryFn: async () => (await api.get('/expedicao/mdfe-status-sefaz')).data,
    retry: false, refetchInterval: 60000,
  })

  const autorizar = useMutation({
    mutationFn: async (id: number) => (await api.post(`/expedicao/mdfe/${id}/autorizar`)).data,
    onSuccess: (r: any) => {
      if (r?.autorizada) toast.success(`MDF-e autorizado (prot. ${r.protocolo})`)
      else toast.error(`Rejeitado: ${r?.cStat} ${r?.xMotivo || r?.erro || ''}`)
      qc.invalidateQueries({ queryKey: ['mdfe'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao autorizar'),
  })
  const encerrar = useMutation({
    mutationFn: async (id: number) => (await api.post(`/expedicao/mdfe/${id}/encerrar`)).data,
    onSuccess: () => { toast.success('MDF-e encerrado'); qc.invalidateQueries({ queryKey: ['mdfe'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao encerrar'),
  })
  const cancelar = useMutation({
    mutationFn: async ({ id, motivo }: { id: number; motivo: string }) =>
      (await api.post(`/expedicao/mdfe/${id}/cancelar`, null, { params: { motivo } })).data,
    onSuccess: () => { toast.success('MDF-e cancelado'); qc.invalidateQueries({ queryKey: ['mdfe'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao cancelar'),
  })

  const sefazOk = sefaz?.cStat === '107' || sefaz?.online
  const totais = {
    docs: Number(data?.soma_docs ?? 0),
    peso: Number(data?.soma_peso ?? 0),   // Decimal vem como string no JSON
    valor: Number(data?.soma_valor ?? 0),
  }
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="text-brand-600" /> MDF-e (Manifesto)
          </h1>
          <p className="text-ink-600 text-sm flex items-center gap-2">
            Manifesto Eletrônico de Documentos Fiscais — transmissão real SEFAZ.
            {sefaz && (
              <span className={`text-xs px-2 py-0.5 rounded ${sefazOk ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                SEFAZ {sefazOk ? 'online' : (sefaz?.cStat || 'offline')}
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowCarga(true)} className="btn-primary">
            <Truck size={14} /> Gerar de Carga
          </button>
          <button onClick={() => setShow(true)} className="btn-secondary">
            <Plus size={14} /> Manual
          </button>
        </div>
      </div>

      {/* KPIs do que está em tela */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={<FileText size={18} />} cor="bg-brand-50 text-brand-600"
          label="Manifestos" valor={total.toLocaleString('pt-BR')}
          sub={status ? `filtro: ${STATUS_META[status]?.label ?? status}` : 'todos os status'} />
        <KpiCard icon={<Boxes size={18} />} cor="bg-violet-50 text-violet-600"
          label="Documentos" valor={totais.docs.toLocaleString('pt-BR')} sub="NF-e manifestadas" />
        <KpiCard icon={<Weight size={18} />} cor="bg-amber-50 text-amber-600"
          label="Peso total" valor={`${totais.peso.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kg`} sub="carga transportada" />
        <KpiCard icon={<Wallet size={18} />} cor="bg-emerald-50 text-emerald-600"
          label="Valor da carga" valor={formatMoney(totais.valor)} sub="somatório das notas" />
      </div>

      <div className="card card-body !py-3">
        <div className="flex gap-2 flex-wrap text-sm items-center">
          <span className="text-ink-500 text-xs font-medium uppercase tracking-wide">Status</span>
          {['', 'rascunho', 'autorizado', 'encerrado', 'cancelado'].map(s => {
            const meta = STATUS_META[s]
            return (
              <button key={s || 'todos'} onClick={() => trocarStatus(s)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition flex items-center gap-1.5 ${status === s ? 'bg-brand-600 text-white shadow-sm' : 'bg-ink-50 text-ink-600 hover:bg-ink-100'}`}>
                {meta && <span className={`w-1.5 h-1.5 rounded-full ${status === s ? 'bg-white' : meta.dot}`} />}
                {s === '' ? 'Todos' : meta.label}
              </button>
            )
          })}
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-ink-500">
              <th>#</th><th>Emissão</th><th>Motorista</th><th>Rota</th><th>Percurso</th>
              <th className="text-right">Docs</th><th className="text-right">Peso</th>
              <th className="text-right">Valor</th>
              <th>Status</th><th className="w-44"></th></tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={10} className="text-center py-10"><Loader2 className="animate-spin inline text-ink-400" /></td></tr>}
            {!isLoading && mdfes.length === 0 && <tr><td colSpan={10} className="text-center py-10 text-ink-400">Nenhum MDF-e.</td></tr>}
            {mdfes.map(m => {
              const meta = STATUS_META[m.status] || { label: m.status, pill: 'bg-ink-100 text-ink-600', dot: 'bg-ink-400' }
              return (
              <tr key={m.id} className="hover:bg-brand-50/40 transition-colors">
                <td className="font-mono font-semibold text-ink-700">{m.numero}<span className="text-ink-400">/{m.serie}</span></td>
                <td className="text-xs text-ink-500 whitespace-nowrap">{formatDateTime(m.data_emissao)}</td>
                <td>
                  {m.motorista_nome ? (
                    <div className="flex items-center gap-2">
                      <span className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold shrink-0 ${avatarCor(m.motorista_nome)}`}>
                        {iniciais(m.motorista_nome)}
                      </span>
                      <div className="leading-tight">
                        <div className="text-sm font-medium text-ink-800 max-w-[150px] truncate" title={m.motorista_nome}>{m.motorista_nome}</div>
                        {m.veiculo_placa && <div className="text-[11px] text-ink-400 font-mono">{m.veiculo_placa}</div>}
                      </div>
                    </div>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-ink-400"><User size={13} /> sem motorista</span>
                  )}
                </td>
                <td className="text-sm whitespace-nowrap">
                  <span className="inline-flex items-center gap-1.5 font-semibold text-ink-700">
                    <span className="px-1.5 py-0.5 rounded bg-ink-100 text-xs">{m.uf_ini}</span>
                    <ArrowRight size={12} className="text-ink-400" />
                    <span className="px-1.5 py-0.5 rounded bg-ink-100 text-xs">{m.uf_fim}</span>
                  </span>
                  {m.cidade_carregamento && <div className="text-[11px] text-ink-400 mt-0.5">{m.cidade_carregamento}</div>}
                </td>
                <td className="text-xs font-mono text-ink-500">{m.percurso.join(' → ') || '—'}</td>
                <td className="text-right tabular-nums">{m.total_documentos}</td>
                <td className="text-right tabular-nums text-ink-600">{Number(m.peso_total_kg).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kg</td>
                <td className="text-right tabular-nums font-medium text-ink-700">{formatMoney(m.valor_total_carga)}</td>
                <td><span className={`text-xs px-2 py-1 rounded-full font-medium inline-flex items-center gap-1.5 ${meta.pill}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />{meta.label}</span></td>
                <td>
                  <div className="flex gap-1 justify-end">
                    {m.status === 'rascunho' && (
                      <button onClick={() => setEditId(m.id)}
                        className="btn-secondary !py-1 !px-2 text-xs text-ink-700" title="Editar (motorista, peso, notas)">
                        <Pencil size={12} />
                      </button>
                    )}
                    {m.status === 'rascunho' && (
                      <button onClick={() => { if (confirm('Transmitir MDF-e para a SEFAZ?')) autorizar.mutate(m.id) }}
                        disabled={autorizar.isPending}
                        className="btn-secondary !py-1 !px-2 text-xs text-sky-700" title="Autorizar na SEFAZ">
                        {autorizar.isPending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                      </button>
                    )}
                    {/* Imprimir/Reimprimir DAMDFE — qualquer um já transmitido */}
                    {(m.status === 'autorizado' || m.status === 'encerrado' || m.status === 'cancelado') && (
                      <button onClick={() => imprimirDamdfe(m.id)}
                        className="btn-secondary !py-1 !px-2 text-xs text-brand-700" title="Imprimir / Reimprimir DAMDFE">
                        <Printer size={12} />
                      </button>
                    )}
                    {m.status === 'autorizado' && (
                      <button onClick={() => { if (confirm('Encerrar MDF-e (fim da viagem)?')) encerrar.mutate(m.id) }}
                        className="btn-secondary !py-1 !px-2 text-xs text-emerald-700" title="Encerrar">
                        <Check size={12} />
                      </button>
                    )}
                    {(m.status === 'rascunho' || m.status === 'autorizado') && (
                      <button onClick={() => {
                        const motivo = prompt('Motivo do cancelamento (mín. 15 caracteres):')
                        if (motivo == null) return
                        if (motivo.trim().length < 15) { toast.error('Justificativa precisa de no mínimo 15 caracteres'); return }
                        cancelar.mutate({ id: m.id, motivo })
                      }} className="btn-secondary !py-1 !px-2 text-xs text-rose-700" title="Cancelar">
                        <Ban size={12} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )})}
          </tbody>
        </table>
        {total > 0 && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-ink-100 text-sm text-ink-600">
            <span className="text-xs">
              {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} de <strong>{total.toLocaleString('pt-BR')}</strong>
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(1)} disabled={page <= 1}
                className="px-2 py-1 rounded text-xs disabled:opacity-40 hover:bg-ink-100">«</button>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-2.5 py-1 rounded text-xs disabled:opacity-40 hover:bg-ink-100">Anterior</button>
              <span className="px-2 text-xs">Pág. <strong>{page}</strong> / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="px-2.5 py-1 rounded text-xs disabled:opacity-40 hover:bg-ink-100">Próxima</button>
              <button onClick={() => setPage(totalPages)} disabled={page >= totalPages}
                className="px-2 py-1 rounded text-xs disabled:opacity-40 hover:bg-ink-100">»</button>
            </div>
          </div>
        )}
      </div>

      {showCarga && <GerarDeCargaModal onClose={() => setShowCarga(false)}
        onDone={() => { setShowCarga(false); qc.invalidateQueries({ queryKey: ['mdfe'] }) }} />}
      {show && <NovoMDFeModal onClose={() => setShow(false)} onDone={() => { setShow(false); qc.invalidateQueries({ queryKey: ['mdfe'] }) }} />}
      {editId && <EditarMDFeModal mdfe={mdfes.find(x => x.id === editId)!} onClose={() => setEditId(null)}
        onDone={() => { setEditId(null); qc.invalidateQueries({ queryKey: ['mdfe'] }) }} />}
    </div>
  )
}

function GerarDeCargaModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [cargaId, setCargaId] = useState<number | ''>('')
  const [veiculoId, setVeiculoId] = useState<number | ''>('')
  const [motoristaId, setMotoristaId] = useState<number | ''>('')
  const [ufFim, setUfFim] = useState('CE')
  const [porUf, setPorUf] = useState(true)              // 1 MDF-e por estado (recomendado p/ multi-UF)
  const [ufsSel, setUfsSel] = useState<Set<string>>(new Set())

  const { data: cargas = [] } = useQuery<any[]>({
    queryKey: ['cargas-mdfe'],
    queryFn: async () => (await api.get('/vendas/cargas/', { params: { tipo: 'distribuicao' } })).data,
  })
  const { data: veiculos = [] } = useQuery<any[]>({
    queryKey: ['veic-mdfe'], queryFn: async () => (await api.get('/expedicao/veiculos')).data,
  })
  const { data: motoristas = [] } = useQuery<any[]>({
    queryKey: ['motoristas-mdfe'],
    queryFn: async () => (await api.get('/expedicao/motoristas')).data,
  })
  // UFs de destino da carga selecionada (pra dividir o manifesto por estado)
  const { data: ufInfo } = useQuery<any>({
    queryKey: ['mdfe-ufs', cargaId],
    queryFn: async () => (await api.get(`/expedicao/mdfe/ufs-da-carga/${cargaId}`)).data,
    enabled: !!cargaId,
  })
  const ufs: any[] = ufInfo?.ufs || []
  const multiUf = ufs.length > 1
  // Quando carrega as UFs, marca todas por padrão e ajusta o uf_fim do modo "tudo num só"
  useEffect(() => {
    if (ufs.length) {
      setUfsSel(new Set(ufs.map(u => u.uf)))
      setUfFim(ufs[0].uf)
    }
  }, [ufInfo])

  const gerar = useMutation({
    mutationFn: async () => {
      // Multi-UF + "por estado": 1 chamada por UF (filtra as notas daquele estado)
      if (porUf && multiUf) {
        const alvo = ufs.filter(u => ufsSel.has(u.uf))
        const ok: any[] = []; const erros: string[] = []
        for (const u of alvo) {
          try {
            const r = (await api.post(`/expedicao/mdfe/from-carga/${cargaId}`, {
              veiculo_id: veiculoId || null, motorista_id: motoristaId || null,
              uf_fim: u.uf, filtrar_por_uf: true,
            })).data
            ok.push({ uf: u.uf, ...r })
          } catch (e: any) { erros.push(`${u.uf}: ${e?.response?.data?.detail || 'erro'}`) }
        }
        return { multi: true, ok, erros }
      }
      // Único MDF-e (1 UF ou "tudo num só")
      const r = (await api.post(`/expedicao/mdfe/from-carga/${cargaId}`, {
        veiculo_id: veiculoId || null, motorista_id: motoristaId || null,
        uf_fim: ufFim, filtrar_por_uf: false,
      })).data
      return { multi: false, ok: [r], erros: [] }
    },
    onSuccess: (d: any) => {
      if (d.multi) {
        toast.success(`${d.ok.length} MDF-e gerados: ${d.ok.map((x: any) => `${x.uf} (${x.documentos})`).join(', ')}`, { duration: 6000 })
        if (d.erros.length) toast.error(d.erros.join(' · '), { duration: 8000 })
      } else {
        toast.success(`MDF-e ${d.ok[0].numero} gerado (${d.ok[0].documentos} NF-e, ${Number(d.ok[0].peso || 0).toFixed(0)} kg)`)
      }
      onDone()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao gerar MDF-e'),
  })

  const toggleUf = (uf: string) => setUfsSel(prev => {
    const n = new Set(prev); n.has(uf) ? n.delete(uf) : n.add(uf); return n
  })
  const nSel = ufs.filter(u => ufsSel.has(u.uf)).length
  const pronto = !!cargaId && (!multiUf || !porUf || nSel > 0)
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold flex items-center gap-2"><Truck size={16} /> Gerar MDF-e de uma Carga</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-ink-500">Puxa as NF-es autorizadas dos pedidos da carga e calcula o peso. Veículo e motorista vêm da carga (deixe em branco).</p>
          <label className="block">
            <div className="text-xs font-medium mb-1">Carga *</div>
            <select className="input w-full" value={cargaId} onChange={e => setCargaId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">— selecione —</option>
              {cargas.map((c: any) => <option key={c.id} value={c.id}>Carga #{c.numero ?? c.id} — {c.status}</option>)}
            </select>
          </label>

          {/* Destinos da carga (UF) — quando entrega em vários estados, gera 1 MDF-e por estado */}
          {cargaId && ufs.length > 0 && (
            <div className="rounded-lg border border-ink-200 bg-ink-50/50 p-3 space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-medium text-ink-700">
                <MapPin size={13} /> Destinos desta carga
              </div>
              <div className="flex flex-wrap gap-1.5">
                {ufs.map(u => (
                  <button type="button" key={u.uf}
                    onClick={() => multiUf && porUf && toggleUf(u.uf)}
                    className={`text-xs px-2 py-1 rounded-full border ${(multiUf && porUf && !ufsSel.has(u.uf)) ? 'bg-white text-ink-400 border-ink-200 line-through' : u.interestadual ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-cyan-100 text-cyan-800 border-cyan-200'}`}>
                    {u.uf} · {u.notas} nota{u.notas > 1 ? 's' : ''}{u.interestadual ? ' ✈' : ''}
                  </button>
                ))}
              </div>
              {multiUf && (
                <label className="flex items-center gap-2 text-xs text-ink-700 cursor-pointer">
                  <input type="checkbox" checked={porUf} onChange={e => setPorUf(e.target.checked)} />
                  <span>Gerar <strong>um MDF-e por estado</strong> ({nSel} selecionado{nSel > 1 ? 's' : ''}) — recomendado quando o motorista entrega em UFs diferentes</span>
                </label>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs font-medium mb-1">Veículo</div>
              <select className="input w-full" value={veiculoId} onChange={e => setVeiculoId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">— da carga —</option>
                {veiculos.map((v: any) => <option key={v.id} value={v.id}>{v.placa}</option>)}
              </select>
            </label>
            {/* UF destino só importa no modo "único MDF-e" */}
            {!(multiUf && porUf) && (
              <label className="block">
                <div className="text-xs font-medium mb-1">UF destino</div>
                <input className="input w-full" maxLength={2} value={ufFim} onChange={e => setUfFim(e.target.value.toUpperCase())} />
              </label>
            )}
          </div>
          <label className="block">
            <div className="text-xs font-medium mb-1">Motorista</div>
            <select className="input w-full" value={motoristaId} onChange={e => setMotoristaId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">— da carga —</option>
              {motoristas.map((mt: any) => <option key={mt.id} value={mt.id}>{mt.nome}</option>)}
            </select>
          </label>
        </div>
        <div className="border-t px-5 py-3 flex justify-end gap-2 bg-ink-50">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button disabled={!pronto || gerar.isPending} onClick={() => gerar.mutate()} className="btn-primary">
            {gerar.isPending && <Loader2 size={14} className="animate-spin" />}
            {multiUf && porUf ? `Gerar ${nSel} MDF-e` : 'Gerar MDF-e'}
          </button>
        </div>
      </div>
    </div>
  )
}

function EditarMDFeModal({ mdfe, onClose, onDone }: { mdfe: MDFe; onClose: () => void; onDone: () => void }) {
  const [motoristaId, setMotoristaId] = useState<number | ''>(mdfe.motorista_id ?? '')
  const [veiculoId, setVeiculoId] = useState<number | ''>(mdfe.veiculo_id ?? '')
  const [peso, setPeso] = useState<string>(String(Number(mdfe.peso_total_kg || 0)))
  const [percurso, setPercurso] = useState<string>((mdfe.percurso || []).join(', '))
  const [sel, setSel] = useState<Set<number>>(new Set())
  const [chavesExt, setChavesExt] = useState<any[]>([])  // NF avulsas por chave (de fora do sistema)
  const [soltas, setSoltas] = useState<any[]>([])        // NF soltas do sistema (sem carga) incluídas
  const [chaveNova, setChaveNova] = useState('')
  const [valorNova, setValorNova] = useState('')
  const [municipioNova, setMunicipioNova] = useState('')
  const [ufNova, setUfNova] = useState('')
  const [buscaSolta, setBuscaSolta] = useState('')

  const { data: motoristas = [] } = useQuery<any[]>({
    queryKey: ['motoristas-mdfe'], queryFn: async () => (await api.get('/expedicao/motoristas')).data,
  })
  const { data: veiculos = [] } = useQuery<any[]>({
    queryKey: ['veic-mdfe'], queryFn: async () => (await api.get('/expedicao/veiculos')).data,
  })
  const { data: docs, isLoading } = useQuery<any>({
    queryKey: ['mdfe-docs', mdfe.id],
    queryFn: async () => (await api.get(`/expedicao/mdfe/${mdfe.id}/documentos`)).data,
  })

  const disponiveis: any[] = docs?.disponiveis || []
  const disponiveisIds = new Set(disponiveis.map(d => d.nfe_emissao_id))
  const [initDone, setInitDone] = useState(false)
  // Inicializa pelo que JÁ está no MDF-e: internos (carga + soltos) viram sel; externos viram chavesExt.
  useEffect(() => {
    if (!docs || initDone) return
    const all: any[] = docs.documentos || []
    const internos = all.filter(d => !d.externa && d.nfe_emissao_id)
    setSel(new Set(internos.map(d => d.nfe_emissao_id)))
    setSoltas(internos.filter(d => !disponiveisIds.has(d.nfe_emissao_id)).map(d => ({
      nfe_emissao_id: d.nfe_emissao_id, nf_numero: null, chave: d.chave,
      cliente: null, uf: d.uf_descarga, cidade: null, valor: d.valor,
    })))
    setChavesExt(all.filter(d => d.externa).map(d => ({
      chave: d.chave, valor: d.valor, peso_kg: d.peso, municipio: d.municipio_descarga, uf: d.uf_descarga,
    })))
    setInitDone(true)
  }, [docs])

  // busca de notas SOLTAS no sistema (sem carga)
  const { data: soltasBusca } = useQuery<any>({
    queryKey: ['mdfe-soltas', mdfe.id, buscaSolta],
    queryFn: async () => (await api.get(`/expedicao/mdfe/${mdfe.id}/nfe-avulsas`, { params: { q: buscaSolta } })).data,
    enabled: buscaSolta.trim().length > 0,
  })
  const resultadosSolta: any[] = (soltasBusca?.nfes || []).filter((n: any) => !sel.has(n.nfe_emissao_id))

  const toggle = (id: number) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  const addSolta = (n: any) => {
    setSel(s => { const x = new Set(s); x.add(n.nfe_emissao_id); return x })
    setSoltas(prev => prev.find(x => x.nfe_emissao_id === n.nfe_emissao_id) ? prev : [...prev, n])
    setBuscaSolta('')
  }
  const removeSolta = (id: number) => {
    setSel(s => { const x = new Set(s); x.delete(id); return x })
    setSoltas(prev => prev.filter(x => x.nfe_emissao_id !== id))
  }
  const addChave = () => {
    const ch = chaveNova.replace(/\D/g, '')
    if (ch.length !== 44) { toast.error('A chave da NF-e deve ter 44 dígitos'); return }
    if (!municipioNova.trim()) { toast.error('Informe a cidade de descarga da nota avulsa'); return }
    if (chavesExt.find(c => String(c.chave).replace(/\D/g, '') === ch)) { toast.error('Chave já incluída'); return }
    setChavesExt(prev => [...prev, { chave: ch, valor: Number(valorNova) || 0, peso_kg: 0, municipio: municipioNova.trim().toUpperCase(), uf: ufNova.trim().toUpperCase() || null }])
    setChaveNova(''); setValorNova(''); setMunicipioNova(''); setUfNova('')
  }
  const updChave = (idx: number, field: string, val: any) => setChavesExt(prev => prev.map((c, i) => i === idx ? { ...c, [field]: val } : c))
  const removeChave = (ch: string) => setChavesExt(prev => prev.filter(c => c.chave !== ch))

  const pesoSel = disponiveis.filter(d => sel.has(d.nfe_emissao_id)).reduce((a, d) => a + Number(d.peso || 0), 0)

  const salvar = useMutation({
    mutationFn: async () => {
      await api.put(`/expedicao/mdfe/${mdfe.id}/documentos`, {
        nfe_emissao_ids: Array.from(sel),
        chaves_externas: chavesExt.map(c => ({
          chave: String(c.chave).replace(/\D/g, ''), valor: Number(c.valor) || 0,
          peso_kg: Number(c.peso_kg) || 0, municipio: c.municipio || null, uf: c.uf || null,
        })),
      })
      await api.patch(`/expedicao/mdfe/${mdfe.id}`, {
        motorista_id: motoristaId || null, veiculo_id: veiculoId || null,
        peso_total_kg: peso ? Number(peso) : null,
        percurso_ufs: percurso.split(/[,/ ]+/).map(s => s.trim()).filter(Boolean),
      })
    },
    onSuccess: () => { toast.success('MDF-e atualizado'); onDone() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold flex items-center gap-2"><Pencil size={16} /> Editar MDF-e #{mdfe.numero}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <div className="text-xs font-medium mb-1">Motorista *</div>
              <select className="input w-full" value={motoristaId} onChange={e => setMotoristaId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">— selecione —</option>
                {motoristas.map((mt: any) => <option key={mt.id} value={mt.id}>{mt.nome}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">Veículo *</div>
              <select className="input w-full" value={veiculoId} onChange={e => setVeiculoId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">—</option>
                {veiculos.map((v: any) => <option key={v.id} value={v.id}>{v.placa}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">Peso total (kg) *</div>
              <input type="number" step="0.001" className="input w-full" value={peso}
                onChange={e => setPeso(e.target.value)} />
              <button type="button" className="text-[11px] text-brand-700 mt-1"
                onClick={() => setPeso(pesoSel.toFixed(3))}>usar peso das notas ({pesoSel.toFixed(0)} kg)</button>
            </label>
          </div>

          <label className="block">
            <div className="text-xs font-medium mb-1 flex items-center gap-1">
              <MapPin size={13} /> Percurso — UFs de transição ({mdfe.uf_ini} → {mdfe.uf_fim})
            </div>
            <input className="input w-full font-mono" placeholder="Ex: PE, BA (vazio se origem/destino forem vizinhas)"
              value={percurso} onChange={e => setPercurso(e.target.value.toUpperCase())} />
            <div className="text-[11px] text-ink-400 mt-1">Preenchido automático pela malha de fronteiras. Ajuste se a rota real passar por outros estados — aparece na seção PERCURSO do DAMDFE.</div>
          </label>

          <div>
            <div className="text-xs font-medium mb-1 flex items-center justify-between">
              <span>Notas no manifesto ({sel.size + chavesExt.length})</span>
              <span className="text-ink-400">da carga + soltas + avulsas (chave)</span>
            </div>
            <div className="border border-ink-200 rounded-lg max-h-64 overflow-y-auto">
              {isLoading && <div className="p-4 text-center text-ink-400"><Loader2 className="animate-spin inline" size={16} /></div>}
              {!isLoading && disponiveis.length === 0 && soltas.length === 0 && chavesExt.length === 0 && <div className="p-4 text-center text-ink-400 text-sm">Nenhuma nota. Inclua abaixo uma nota solta ou avulsa por chave.</div>}
              <table className="w-full text-sm">
                <tbody>
                  {/* avulsas por chave (de fora do sistema) — cidade/UF editável (obrigatório p/ não rejeitar) */}
                  {chavesExt.map((d, idx) => (
                    <tr key={`ext-${d.chave}`} className="border-b border-ink-100 bg-amber-50">
                      <td className="px-2 py-1.5 w-8 text-center" title={`NF avulsa: ${d.chave}`}>📎</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">…{String(d.chave).slice(-10)}<span className="ml-1 text-amber-700 font-medium">avulsa</span></td>
                      <td className="px-2 py-1.5">
                        <input className={`input !py-0.5 !text-xs ${!d.municipio ? '!border-rose-300 bg-rose-50' : ''}`} placeholder="cidade de descarga *"
                          value={d.municipio || ''} onChange={e => updChave(idx, 'municipio', e.target.value.toUpperCase())} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input className="input !py-0.5 !text-xs w-12 text-center" maxLength={2} placeholder="UF"
                          value={d.uf || ''} onChange={e => updChave(idx, 'uf', e.target.value.toUpperCase())} />
                      </td>
                      <td className="px-2 py-1.5 text-right">{formatMoney(d.valor)}</td>
                      <td className="px-2 py-1.5 text-right w-8"><button type="button" onClick={() => removeChave(d.chave)} className="text-rose-500 hover:text-rose-700"><X size={14} /></button></td>
                    </tr>
                  ))}
                  {/* notas soltas do sistema incluídas */}
                  {soltas.map(d => (
                    <tr key={`solta-${d.nfe_emissao_id}`} className="border-b border-ink-100 bg-emerald-50/60">
                      <td className="px-2 py-1.5 w-8 text-center" title="NF solta do sistema (sem carga)">🏷️</td>
                      <td className="px-2 py-1.5 font-mono text-xs">{d.nf_numero ? `NF ${d.nf_numero}` : <span className="text-[10px]">{String(d.chave).slice(-8)}</span>}</td>
                      <td className="px-2 py-1.5">{d.cliente || <span className="text-ink-400">solta</span>}{d.cidade && <span className="text-[11px] text-ink-400 ml-1">· {d.cidade}</span>}</td>
                      <td className="px-2 py-1.5 text-center">{d.uf && <span className="text-[11px] px-1.5 py-0.5 rounded bg-ink-100 text-ink-600 font-medium">{d.uf}</span>}</td>
                      <td className="px-2 py-1.5 text-right">{formatMoney(d.valor)}</td>
                      <td className="px-2 py-1.5 text-right w-8"><button type="button" onClick={() => removeSolta(d.nfe_emissao_id)} className="text-rose-500 hover:text-rose-700"><X size={14} /></button></td>
                    </tr>
                  ))}
                  {/* notas da carga (checkbox) */}
                  {disponiveis.map(d => (
                    <tr key={d.nfe_emissao_id} className={`border-b border-ink-100 ${sel.has(d.nfe_emissao_id) ? 'bg-brand-50' : ''}`}>
                      <td className="px-2 py-1.5 w-8"><input type="checkbox" checked={sel.has(d.nfe_emissao_id)} onChange={() => toggle(d.nfe_emissao_id)} /></td>
                      <td className="px-2 py-1.5 font-mono text-xs">NF {d.numero}</td>
                      <td className="px-2 py-1.5">
                        {d.cliente || `Pedido ${d.pedido_numero}`}
                        {d.cidade && <span className="text-[11px] text-ink-400 ml-1">· {d.cidade}</span>}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        {d.uf && <span className="text-[11px] px-1.5 py-0.5 rounded bg-ink-100 text-ink-600 font-medium">{d.uf}</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right">{formatMoney(d.valor)}</td>
                      <td className="px-2 py-1.5 w-8"></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* incluir nota SOLTA do sistema (sem carga) */}
          <div>
            <div className="text-xs font-medium mb-1 flex items-center gap-1"><Search size={13} /> Incluir nota solta (do sistema, sem carga)</div>
            <input className="input w-full" placeholder="Buscar por nº da NF, nº do pedido ou cliente…"
              value={buscaSolta} onChange={e => setBuscaSolta(e.target.value)}
              onKeyDown={enterAbrePrimeiro(buscaSolta.trim() ? resultadosSolta : [], addSolta)} />
            {buscaSolta.trim() && (
              <div className="border border-ink-200 rounded-lg mt-1 max-h-40 overflow-y-auto">
                {resultadosSolta.length === 0 ? <div className="p-3 text-center text-ink-400 text-xs">Nenhuma nota solta encontrada.</div> : (
                  <table className="w-full text-sm"><tbody>
                    {resultadosSolta.map((n: any) => (
                      <tr key={n.nfe_emissao_id} className="border-b border-ink-50 hover:bg-emerald-50 cursor-pointer" onClick={() => addSolta(n)}>
                        <td className="px-2 py-1.5 w-8 text-emerald-600"><Plus size={14} /></td>
                        <td className="px-2 py-1.5 font-mono text-xs">{n.nf_numero ? `NF ${n.nf_numero}` : `Ped ${n.pedido_numero}`}</td>
                        <td className="px-2 py-1.5">{n.cliente || '—'}{n.cidade && <span className="text-[11px] text-ink-400 ml-1">· {n.cidade}</span>}</td>
                        <td className="px-2 py-1.5 text-center">{n.uf && <span className="text-[11px] px-1.5 py-0.5 rounded bg-ink-100 text-ink-600 font-medium">{n.uf}</span>}</td>
                        <td className="px-2 py-1.5 text-right">{formatMoney(n.valor)}</td>
                      </tr>
                    ))}
                  </tbody></table>
                )}
              </div>
            )}
          </div>

          {/* incluir nota AVULSA por chave (de fora do sistema) */}
          <div>
            <div className="text-xs font-medium mb-1 flex items-center gap-1"><FileText size={13} /> Incluir nota avulsa por chave (de fora do sistema)</div>
            <div className="flex gap-2">
              <input className="input flex-1 font-mono text-xs" placeholder="Chave de acesso da NF-e (44 dígitos)"
                value={chaveNova} onChange={e => setChaveNova(e.target.value)} />
              <input className="input w-24" type="number" step="0.01" placeholder="Valor R$"
                value={valorNova} onChange={e => setValorNova(e.target.value)} />
            </div>
            <div className="flex gap-2 mt-2">
              <input className="input flex-1" placeholder="Cidade de descarga * (ex.: BARBALHA)"
                value={municipioNova} onChange={e => setMunicipioNova(e.target.value.toUpperCase())} />
              <input className="input w-16 text-center" maxLength={2} placeholder="UF"
                value={ufNova} onChange={e => setUfNova(e.target.value.toUpperCase())} />
              <button type="button" onClick={addChave} className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium whitespace-nowrap">Adicionar</button>
            </div>
            <div className="text-[11px] text-ink-400 mt-1">A <strong>cidade de descarga é obrigatória</strong> (vira o código IBGE do município no MDF-e — sem ela a SEFAZ rejeita). UF é deduzida da chave se vazia.</div>
          </div>
        </div>
        <div className="border-t px-5 py-3 flex justify-end gap-2 bg-ink-50">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button disabled={salvar.isPending || !motoristaId || !veiculoId || (sel.size === 0 && chavesExt.length === 0)}
            onClick={() => salvar.mutate()} className="btn-primary">
            {salvar.isPending && <Loader2 size={14} className="animate-spin" />} Salvar
          </button>
        </div>
      </div>
    </div>
  )
}

function NovoMDFeModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({
    serie: 1,
    uf_ini: 'CE', uf_fim: 'PI',
    cidade_carregamento: '', cidade_descarregamento: '',
    rota_id: '' as number | '',
    veiculo_id: '' as number | '',
    motorista_id: '' as number | '',
    transportadora_id: '' as number | '',
    tipo_transporte: 'proprio',
    percurso_ufs: '' as string,  // CSV
    observacoes: '',
  })
  const [docs, setDocs] = useState<Array<{ chave_nfe: string; peso_kg: string; valor: string; municipio_descarga: string; uf_descarga: string }>>([
    { chave_nfe: '', peso_kg: '0', valor: '0', municipio_descarga: '', uf_descarga: '' },
  ])
  const set = (k: string, v: any) => setForm(p => ({ ...p, [k]: v }))

  const { data: veiculos = [] } = useQuery<any[]>({
    queryKey: ['veic-mdfe'], queryFn: async () => (await api.get('/expedicao/veiculos')).data,
  })
  const { data: rotas = [] } = useQuery<any[]>({
    queryKey: ['rotas-mdfe'], queryFn: async () => (await api.get('/expedicao/rotas')).data,
  })
  const { data: transportadores = [] } = useQuery<any[]>({
    queryKey: ['transp'],
    queryFn: async () => (await api.get('/cadastros/pessoas', { params: { limit: 500 } })).data,
  })
  const transpFiltrados = transportadores.filter((p: any) => p.eh_transportador)

  const criar = useMutation({
    mutationFn: async () => (await api.post('/expedicao/mdfe', {
      serie: form.serie,
      uf_ini: form.uf_ini, uf_fim: form.uf_fim,
      cidade_carregamento: form.cidade_carregamento || null,
      cidade_descarregamento: form.cidade_descarregamento || null,
      rota_id: form.rota_id || null,
      veiculo_id: form.veiculo_id,
      motorista_id: form.motorista_id || null,
      transportadora_id: form.transportadora_id || null,
      tipo_transporte: form.tipo_transporte,
      percurso_ufs: form.percurso_ufs.split(/[, ]+/).filter(Boolean),
      documentos: docs.filter(d => d.chave_nfe).map(d => ({
        chave_nfe: d.chave_nfe,
        peso_kg: parseFloat(d.peso_kg) || 0,
        valor: parseFloat(d.valor) || 0,
        municipio_descarga: d.municipio_descarga || null,
        uf_descarga: d.uf_descarga || null,
      })),
      observacoes: form.observacoes || null,
    })).data,
    onSuccess: (m: MDFe) => { toast.success(`MDF-e ${m.numero}/${m.serie} criado`); onDone() },
  })

  const pronto = form.veiculo_id && form.uf_ini && form.uf_fim

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] overflow-auto">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold">Novo MDF-e (manual)</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-4 gap-3">
            <label className="block">
              <div className="text-xs font-medium mb-1">Série</div>
              <input type="number" className="input w-full" value={form.serie} onChange={e => set('serie', parseInt(e.target.value) || 1)} />
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">UF início *</div>
              <input className="input w-full" maxLength={2} value={form.uf_ini} onChange={e => set('uf_ini', e.target.value.toUpperCase())} />
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">UF fim *</div>
              <input className="input w-full" maxLength={2} value={form.uf_fim} onChange={e => set('uf_fim', e.target.value.toUpperCase())} />
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">Tipo</div>
              <select className="input w-full" value={form.tipo_transporte} onChange={e => set('tipo_transporte', e.target.value)}>
                <option value="proprio">Próprio</option>
                <option value="terceiro">Terceiro</option>
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs font-medium mb-1">Cidade carregamento</div>
              <input className="input w-full" value={form.cidade_carregamento} onChange={e => set('cidade_carregamento', e.target.value)} />
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">Cidade descarregamento</div>
              <input className="input w-full" value={form.cidade_descarregamento} onChange={e => set('cidade_descarregamento', e.target.value)} />
            </label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <div className="text-xs font-medium mb-1">Veículo *</div>
              <select className="input w-full" value={form.veiculo_id} onChange={e => set('veiculo_id', e.target.value ? Number(e.target.value) : '')}>
                <option value="">—</option>
                {veiculos.map((v: any) => <option key={v.id} value={v.id}>{v.placa} ({v.tipo_rodado})</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">Rota</div>
              <select className="input w-full" value={form.rota_id} onChange={e => set('rota_id', e.target.value ? Number(e.target.value) : '')}>
                <option value="">— opcional —</option>
                {rotas.map((r: any) => <option key={r.id} value={r.id}>{r.nome}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">Transportadora</div>
              <select className="input w-full" value={form.transportadora_id} onChange={e => set('transportadora_id', e.target.value ? Number(e.target.value) : '')}>
                <option value="">— opcional —</option>
                {transpFiltrados.map((p: any) => <option key={p.id} value={p.id}>{p.nome}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <div className="text-xs font-medium mb-1">Percurso (UFs intermediárias, separadas por vírgula)</div>
            <input className="input w-full font-mono" placeholder="Ex: PE, BA" value={form.percurso_ufs} onChange={e => set('percurso_ufs', e.target.value.toUpperCase())} />
          </label>

          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold">Documentos vinculados (NFes)</div>
              <button onClick={() => setDocs([...docs, { chave_nfe: '', peso_kg: '0', valor: '0', municipio_descarga: '', uf_descarga: '' }])}
                className="btn-secondary !py-1 !px-2 text-xs"><Plus size={12} /> Doc</button>
            </div>
            <div className="space-y-2">
              {docs.map((d, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end p-2 border rounded bg-ink-50">
                  <div className="col-span-6">
                    <div className="text-[10px] uppercase text-ink-500 mb-0.5">Chave NFe (44)</div>
                    <input className="input w-full text-xs font-mono" value={d.chave_nfe}
                      onChange={e => setDocs(docs.map((x, i) => i === idx ? { ...x, chave_nfe: e.target.value } : x))} maxLength={44} />
                  </div>
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase text-ink-500 mb-0.5">Peso kg</div>
                    <input type="number" className="input w-full text-sm" value={d.peso_kg}
                      onChange={e => setDocs(docs.map((x, i) => i === idx ? { ...x, peso_kg: e.target.value } : x))} />
                  </div>
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase text-ink-500 mb-0.5">Valor</div>
                    <input type="number" step="0.01" className="input w-full text-sm" value={d.valor}
                      onChange={e => setDocs(docs.map((x, i) => i === idx ? { ...x, valor: e.target.value } : x))} />
                  </div>
                  <div className="col-span-1">
                    <div className="text-[10px] uppercase text-ink-500 mb-0.5">UF desc.</div>
                    <input className="input w-full text-xs" maxLength={2} value={d.uf_descarga}
                      onChange={e => setDocs(docs.map((x, i) => i === idx ? { ...x, uf_descarga: e.target.value.toUpperCase() } : x))} />
                  </div>
                  <div className="col-span-1 text-right">
                    {docs.length > 1 && (
                      <button onClick={() => setDocs(docs.filter((_, i) => i !== idx))} className="text-rose-600"><X size={12} /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <label className="block">
            <div className="text-xs font-medium mb-1">Observações</div>
            <textarea className="input w-full" rows={2} value={form.observacoes} onChange={e => set('observacoes', e.target.value)} />
          </label>
        </div>
        <div className="border-t px-5 py-3 flex justify-end gap-2 bg-ink-50">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button disabled={!pronto || criar.isPending} onClick={() => criar.mutate()} className="btn-primary">
            {criar.isPending && <Loader2 size={14} className="animate-spin" />}
            Criar MDF-e
          </button>
        </div>
      </div>
    </div>
  )
}
